import { BudgetType } from '../../../../types/authenticateSmartcheckSessionDto'

export type BudgetKeyWithPossibleRentIncluded =
	| 'additionalFixed'
	| 'uncategorizedPaymentServices'
	| 'rentIsNotIncludedInOtherExpenses'

export const MissingInfoBudgetKeys = {
	Insurance: 'tree.expenses.others.insurance',
	Rent: 'tree.expenses.housing.rent',
	Salary: 'tree.income.salary',
	CarLoanAndLeasing: 'tree.expenses.transport.carLoanAndLeasing',
	UnionFees: 'tree.expenses.others.unionFees',
	PhoneAndBroadband: 'tree.expenses.others.phoneAndBroadband',
} as const

export type MissingInfoBudgetKey =
	(typeof MissingInfoBudgetKeys)[keyof typeof MissingInfoBudgetKeys]

const MINIMUM_RENT_AMOUNT = 2500

const isExpenseGreaterThanOrEqualToMinimumRent = (expense: number) =>
	expense >= MINIMUM_RENT_AMOUNT

export const getListOfCategoriesWithPossibleRentIncluded = (estimates: any) => {
	const budgetKeysWithPossibleRentIncluded: BudgetKeyWithPossibleRentIncluded[] =
		[]
	if (
		isExpenseGreaterThanOrEqualToMinimumRent(
			Math.abs(estimates.tree.expenses.others.additionalFixed),
		)
	) {
		budgetKeysWithPossibleRentIncluded.push('additionalFixed')
	}
	if (
		isExpenseGreaterThanOrEqualToMinimumRent(
			Math.abs(estimates.tree.expenses.others.uncategorizedPaymentServices),
		)
	) {
		budgetKeysWithPossibleRentIncluded.push('uncategorizedPaymentServices')
	}

	return budgetKeysWithPossibleRentIncluded
}

export interface ValidationDetails {
	reason: string
	actualValue: number
	thresholdValue: number
	additionalInfo?: Record<string, any>
}

export function isBudgetValueValid(
	key: MissingInfoBudgetKey,
	budgetEstimates: any,
	useMinimumRentAmount?: boolean,
	miniBudgetType?: BudgetType,
): boolean
export function isBudgetValueValid(
	key: MissingInfoBudgetKey,
	budgetEstimates: any,
	useMinimumRentAmount: boolean,
	miniBudgetType: BudgetType | undefined,
	returnDetails: true,
): { isValid: boolean; details?: ValidationDetails }
export function isBudgetValueValid(
	key: MissingInfoBudgetKey,
	budgetEstimates: any,
	useMinimumRentAmount = true,
	miniBudgetType?: BudgetType,
	returnDetails = false,
): boolean | { isValid: boolean; details?: ValidationDetails } {
	const createResult = (isValid: boolean, details?: ValidationDetails) => {
		return returnDetails
			? { isValid, details: !isValid ? details : undefined }
			: isValid
	}

	switch (key) {
		case MissingInfoBudgetKeys.Rent: {
			const rentAmount = Math.abs(budgetEstimates.tree.expenses.housing.rent)
			const threshold = useMinimumRentAmount ? MINIMUM_RENT_AMOUNT : 1
			const isNotRenting =
				!budgetEstimates.insights?.isRenting &&
				!budgetEstimates.insights?.isCooperativeHousing
			const hasMinimumRent = rentAmount >= threshold
			const isRentIncluded =
				budgetEstimates.insights
					?.isRentIncludedInExpensesOthersAdditionalFixed ||
				budgetEstimates.insights
					?.isRentIncludedInExpensesOthersUncategorizedPaymentServices

			const isValid = isNotRenting || hasMinimumRent || isRentIncluded

			if (returnDetails) {
				return createResult(isValid, {
					reason: 'Rent amount is below minimum threshold',
					actualValue: rentAmount,
					thresholdValue: threshold,
					additionalInfo: {
						isRenting: budgetEstimates.insights?.isRenting,
						isCooperativeHousing:
							budgetEstimates.insights?.isCooperativeHousing,
						isRentIncluded,
					},
				})
			}
			return createResult(isValid)
		}
		case MissingInfoBudgetKeys.Insurance: {
			const insuranceAmount = Math.abs(
				budgetEstimates.tree.expenses.others.insurance,
			)
			const isV1 = miniBudgetType === BudgetType.V1
			const hasInsurance = insuranceAmount > 0

			const isValid = isV1 || hasInsurance

			if (returnDetails) {
				return createResult(isValid, {
					reason: 'Insurance amount is zero and budget type is not V1',
					actualValue: insuranceAmount,
					thresholdValue: 0,
					additionalInfo: {
						budgetType: miniBudgetType,
					},
				})
			}
			return createResult(isValid)
		}
		case MissingInfoBudgetKeys.Salary: {
			const salaryAmount = Math.abs(budgetEstimates.tree.income.salary)
			const threshold = 1000
			const isValid = salaryAmount >= threshold

			if (returnDetails) {
				return createResult(isValid, {
					reason: 'Salary amount is below minimum threshold',
					actualValue: salaryAmount,
					thresholdValue: threshold,
				})
			}
			return createResult(isValid)
		}
		case MissingInfoBudgetKeys.CarLoanAndLeasing: {
			const carAmount = Math.abs(
				budgetEstimates.tree.expenses.transport.carLoanAndLeasing,
			)
			const isNotLeasingCar = !budgetEstimates.insights?.isLeasingCar
			const hasCarExpense = carAmount > 0

			const isValid = isNotLeasingCar || hasCarExpense

			if (returnDetails) {
				return createResult(isValid, {
					reason: 'Car leasing detected but no car loan/leasing expense found',
					actualValue: carAmount,
					thresholdValue: 0,
					additionalInfo: {
						isLeasingCar: budgetEstimates.insights?.isLeasingCar,
					},
				})
			}
			return createResult(isValid)
		}
		case MissingInfoBudgetKeys.UnionFees: {
			const unionFeesAmount = Math.abs(
				budgetEstimates.tree.expenses.others.unionFees,
			)
			const hasNoAKasse = !budgetEstimates.insights?.hasPaymentsToAKasse
			const hasUnionFees = unionFeesAmount > 0

			const isValid = hasNoAKasse || hasUnionFees

			if (returnDetails) {
				return createResult(isValid, {
					reason: 'A-kasse payments detected but no union fees found',
					actualValue: unionFeesAmount,
					thresholdValue: 0,
					additionalInfo: {
						hasPaymentsToAKasse: budgetEstimates.insights?.hasPaymentsToAKasse,
					},
				})
			}
			return createResult(isValid)
		}
		case MissingInfoBudgetKeys.PhoneAndBroadband: {
			const phoneOthersAmount = Math.abs(
				budgetEstimates.tree.expenses.others.phoneAndBroadband,
			)
			const phoneHousingAmount = Math.abs(
				budgetEstimates.tree.expenses.housing.phoneAndBroadband,
			)
			const hasFreePhone = budgetEstimates.insights?.hasFreePhone
			const hasPhoneExpense = phoneOthersAmount > 0 || phoneHousingAmount > 0

			const isValid = hasFreePhone || hasPhoneExpense

			if (returnDetails) {
				return createResult(isValid, {
					reason:
						'No phone/broadband expense found and no free phone indicated',
					actualValue: phoneOthersAmount + phoneHousingAmount,
					thresholdValue: 0,
					additionalInfo: {
						hasFreePhone,
						phoneOthersAmount,
						phoneHousingAmount,
					},
				})
			}
			return createResult(isValid)
		}
		default:
			return createResult(true)
	}
}
